// Shop Revenue 대시보드의 표/차트 파트. 상태·페치는 ShopRevenuePage 가 담당하고
// 여기서는 집계 결과(RevenueAgg) + flavor 분해(RevenueSplit) 를 그리기만 한다.

import type { RevenueAgg } from '@rb/shared/analytics/revenue-agg';
import {
  splitOf,
  type FlavorSplit,
  type PayerProductRow,
  type RevenueSplit,
} from '@rb/shared/analytics/revenue-split';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const usd = (n: number): string =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (n: number): string => `${(n * 100).toFixed(1)}%`;

/** web2/web3 막대 색 — 그레이스케일 테마에서도 구분되도록 primary vs chart-2. */
const W2_BAR = 'bg-primary';
const W3_BAR = 'bg-chart-2';

/** "W2 $x · W3 $y" 보조 표기. showSplit=false 면 표기 자체를 생략한다. */
function splitSub(split: FlavorSplit, pick: (s: FlavorSplit['web2']) => number): string {
  return `W2 ${usd(pick(split.web2))} · W3 ${usd(pick(split.web3))}`;
}

export function KpiCard({
  label,
  value,
  sub,
  highlight,
  danger,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
  danger?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span
          className={[
            'text-xl font-semibold tabular-nums',
            danger ? 'text-destructive' : highlight ? 'text-primary' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {value}
        </span>
        {sub && <span className="text-xs text-muted-foreground tabular-nums">{sub}</span>}
      </CardContent>
    </Card>
  );
}

export function RevenueKpiRow({
  agg,
  split,
  showSplit,
}: {
  agg: RevenueAgg;
  split: RevenueSplit;
  showSplit: boolean;
}) {
  const t = split.totals;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      <KpiCard
        label="총매출 (gross)"
        value={usd(agg.grossUsd)}
        sub={showSplit ? splitSub(t, (s) => s.grossUsd) : undefined}
      />
      <KpiCard
        label="순매출 (net)"
        value={usd(agg.netUsd)}
        sub={showSplit ? splitSub(t, (s) => s.netUsd) : undefined}
        highlight
      />
      <KpiCard
        label="환불"
        value={usd(agg.refundUsd)}
        sub={`${agg.refundCount.toLocaleString()}건 · ${pct(agg.refundRate)}`}
        danger
      />
      <KpiCard
        label="결제 건수"
        value={`${agg.count.toLocaleString()}건`}
        sub={
          showSplit
            ? `W2 ${t.web2.count.toLocaleString()}건 · W3 ${t.web3.count.toLocaleString()}건`
            : undefined
        }
      />
      <KpiCard
        label="Payers"
        value={`${agg.payers.toLocaleString()}명`}
        sub={
          showSplit
            ? `W2 ${t.web2.payers.toLocaleString()}명 · W3 ${t.web3.payers.toLocaleString()}명`
            : undefined
        }
      />
      <KpiCard label="ARPPU" value={usd(agg.arppuUsd)} highlight />
    </div>
  );
}

/** 막대 범례 — web2/web3 색 대응. */
function BarLegend() {
  return (
    <span className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="flex items-center gap-1">
        <span className={`inline-block h-2 w-3 rounded-sm ${W2_BAR}`} /> web2
      </span>
      <span className="flex items-center gap-1">
        <span className={`inline-block h-2 w-3 rounded-sm ${W3_BAR}`} /> web3
      </span>
    </span>
  );
}

export function DailyTrendCard({
  agg,
  split,
  showSplit,
}: {
  agg: RevenueAgg;
  split: RevenueSplit;
  showSplit: boolean;
}) {
  const maxDayNet = agg.byDay.reduce((m, d) => Math.max(m, d.netUsd), 0);
  const widthPct = (v: number): string => (maxDayNet > 0 ? `${(v / maxDayNet) * 100}%` : '0%');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">일별 추세 (순매출)</CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>막대 = net, 보조 표기 = gross.</span>
          {showSplit && <BarLegend />}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {agg.byDay.length === 0 ? (
          <p className="text-sm text-muted-foreground">데이터 없음</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {agg.byDay.map((d) => {
              const s = splitOf(split.byDay, d.date);
              return (
                <div key={d.date} className="flex items-center gap-3 text-xs">
                  <span className="w-24 shrink-0 font-mono text-muted-foreground">{d.date}</span>
                  <div className="relative h-4 flex-1 overflow-hidden rounded bg-muted">
                    {showSplit ? (
                      <div className="absolute inset-y-0 left-0 flex w-full">
                        <div className={W2_BAR} style={{ width: widthPct(s.web2.netUsd) }} />
                        <div className={W3_BAR} style={{ width: widthPct(s.web3.netUsd) }} />
                      </div>
                    ) : (
                      <div
                        className={`absolute inset-y-0 left-0 rounded ${W2_BAR}`}
                        style={{ width: widthPct(d.netUsd) }}
                      />
                    )}
                  </div>
                  {showSplit && (
                    <span className="w-52 shrink-0 text-right font-mono text-muted-foreground">
                      W2 {usd(s.web2.netUsd)} · W3 {usd(s.web3.netUsd)}
                    </span>
                  )}
                  <span className="w-44 shrink-0 text-right font-mono">
                    {usd(d.netUsd)}
                    <span className="ml-1 text-muted-foreground">/ {usd(d.grossUsd)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ProductRevenueCard({
  agg,
  split,
  showSplit,
}: {
  agg: RevenueAgg;
  split: RevenueSplit;
  showSplit: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">상품별 매출</CardTitle>
        <CardDescription>
          순매출(net) 내림차순. 비중 = 상품 net / 전체 net.
          {showSplit && ' web2/web3 열은 각 verse 의 순매출(건수).'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {agg.byProduct.length === 0 ? (
          <p className="text-sm text-muted-foreground">데이터 없음</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>productId</TableHead>
                <TableHead>name</TableHead>
                <TableHead className="text-right">건수</TableHead>
                {showSplit && <TableHead className="text-right">web2</TableHead>}
                {showSplit && <TableHead className="text-right">web3</TableHead>}
                <TableHead className="text-right">합계 순매출</TableHead>
                <TableHead className="text-right">비중</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agg.byProduct.map((p) => {
                const s = splitOf(split.byProduct, p.productId);
                return (
                  <TableRow key={p.productId}>
                    <TableCell className="font-mono text-xs">{p.productId}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell className="text-right">{p.count.toLocaleString()}</TableCell>
                    {showSplit && <FlavorCell stat={s.web2} />}
                    {showSplit && <FlavorCell stat={s.web3} />}
                    <TableCell className="text-right font-mono">{usd(p.netUsd)}</TableCell>
                    <TableCell className="text-right">
                      {agg.netUsd > 0 ? pct(p.netUsd / agg.netUsd) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

/** "순매출 (건수)" 한 칸. 매출 0 이면 흐리게 — 어느 verse 전용 상품인지 한눈에 보이도록. */
function FlavorCell({ stat }: { stat: FlavorSplit['web2'] }) {
  return (
    <TableCell
      className={`text-right font-mono ${stat.count === 0 ? 'text-muted-foreground/50' : ''}`}
    >
      {usd(stat.netUsd)}
      <span className="ml-1 text-xs text-muted-foreground">({stat.count.toLocaleString()})</span>
    </TableCell>
  );
}

/** payer 결제의 구성 품목 — net 내림차순 상위 몇 개만 칩으로, 나머지는 "+N" 로 접는다. */
const PAYER_PRODUCT_CHIPS = 4;

function PayerProductsCell({ rows }: { rows: PayerProductRow[] }) {
  if (rows.length === 0) return <TableCell className="text-muted-foreground/50">-</TableCell>;
  const shown = rows.slice(0, PAYER_PRODUCT_CHIPS);
  const rest = rows.length - shown.length;
  return (
    <TableCell>
      <span className="flex flex-wrap gap-1">
        {shown.map((r) => (
          <span
            key={r.productId}
            className="rounded border px-1.5 py-0.5 text-xs whitespace-nowrap"
            title={`${r.productId} · ${r.count.toLocaleString()}건 · ${usd(r.netUsd)}`}
          >
            {r.name}
            <span className="ml-1 text-muted-foreground tabular-nums">
              {usd(r.netUsd)} ({r.count.toLocaleString()})
            </span>
          </span>
        ))}
        {rest > 0 && (
          <span
            className="rounded border border-dashed px-1.5 py-0.5 text-xs text-muted-foreground"
            title={rows
              .slice(PAYER_PRODUCT_CHIPS)
              .map((r) => `${r.name} ${usd(r.netUsd)} (${r.count})`)
              .join('\n')}
          >
            +{rest}
          </span>
        )}
      </span>
    </TableCell>
  );
}

/** 닉네임 조회 상태 — 페이지가 useGameNicknames 결과를 얇게 감싸 넘긴다. */
export interface NicknameView {
  /** payer 의 인게임 닉네임(Name#0000). 아직 못 찾았으면 null. */
  of: (uid: string, wallet: string) => string | null;
  loading: boolean;
  progress: { done: number; total: number } | null;
  error: string;
  failed: number;
  retry: () => void;
}

/**
 * payer 닉네임 — 지갑에 연결된 **인게임** 닉네임(Name#0000).
 * V8 프로필 표시명은 게임 닉네임과 다른 값이라 tooltip 보조로만 남긴다.
 * 아직 조회 중이면 '조회 중', 해당 verse 에 게임 계정이 없으면 '-'.
 */
function PayerNameCell({
  payer,
  nickname,
}: {
  payer: RevenueAgg['topPayers'][number];
  nickname?: NicknameView;
}) {
  const gameNickname = nickname?.of(payer.uid, payer.wallet) ?? null;
  const profileHint = [payer.displayName, payer.name].filter(Boolean).join(' · ');
  return (
    <TableCell className="max-w-56">
      {gameNickname ? (
        <span
          className="block truncate"
          title={profileHint ? `${gameNickname} (V8: ${profileHint})` : gameNickname}
        >
          {gameNickname}
        </span>
      ) : nickname?.loading ? (
        <span className="text-xs text-muted-foreground">조회 중…</span>
      ) : (
        <span className="text-xs text-muted-foreground" title={profileHint || undefined}>
          -
        </span>
      )}
    </TableCell>
  );
}

/** 닉네임 조회 진행/실패 표시 — 표 자체는 조회와 무관하게 이미 그려져 있다. */
function NicknameStatus({ nickname }: { nickname: NicknameView }) {
  if (nickname.loading) {
    return (
      <p className="text-xs text-muted-foreground">
        인게임 닉네임 조회 중…{' '}
        {nickname.progress ? `${nickname.progress.done}/${nickname.progress.total}` : ''}
      </p>
    );
  }
  if (nickname.error) {
    return (
      <p className="text-xs text-destructive">
        닉네임 조회 실패: {nickname.error}{' '}
        <button type="button" className="underline" onClick={nickname.retry}>
          다시 시도
        </button>
      </p>
    );
  }
  if (nickname.failed > 0) {
    return (
      <p className="text-xs text-muted-foreground">
        닉네임 {nickname.failed.toLocaleString()}건 조회 실패(나머지는 정상).{' '}
        <button type="button" className="underline" onClick={nickname.retry}>
          다시 시도
        </button>
      </p>
    );
  }
  return null;
}

/** payer 가 속한 verse — 두 verse 모두 결제가 있으면 둘 다 표기(정상적으로는 단일). */
function payerFlavors(s: FlavorSplit): ('web2' | 'web3')[] {
  const out: ('web2' | 'web3')[] = [];
  if (s.web2.count > 0) out.push('web2');
  if (s.web3.count > 0) out.push('web3');
  return out;
}

export function TopPayersCard({
  agg,
  split,
  showSplit,
  nickname,
}: {
  agg: RevenueAgg;
  split: RevenueSplit;
  showSplit: boolean;
  nickname?: NicknameView;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">VX 사용 상위 50</CardTitle>
        <CardDescription>
          순매출(net) 내림차순. 닉네임 = 지갑에 연결된 인게임 닉네임(해당 verse 게임 계정 기준).
          구성 품목 = 그 금액을 채운 상품별 순매출(건수), 상위 {PAYER_PRODUCT_CHIPS}개 표시.
          {showSplit && ' verse 열 = 해당 결제가 발생한 web2/web3 구분.'}
        </CardDescription>
        {nickname && <NicknameStatus nickname={nickname} />}
      </CardHeader>
      <CardContent>
        {agg.topPayers.length === 0 ? (
          <p className="text-sm text-muted-foreground">데이터 없음</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 text-right">#</TableHead>
                <TableHead>닉네임</TableHead>
                <TableHead>지갑 / 식별</TableHead>
                {showSplit && <TableHead className="w-24">verse</TableHead>}
                <TableHead>구성 품목</TableHead>
                <TableHead className="text-right">건수</TableHead>
                {showSplit && <TableHead className="text-right">web2</TableHead>}
                {showSplit && <TableHead className="text-right">web3</TableHead>}
                <TableHead className="text-right">합계 순매출</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agg.topPayers.map((p, i) => {
                const s = splitOf(split.byPayer, p.uid);
                return (
                  <TableRow key={p.uid}>
                    <TableCell className="text-right text-muted-foreground">{i + 1}</TableCell>
                    <PayerNameCell payer={p} nickname={nickname} />
                    <TableCell className="font-mono text-xs break-all">
                      {p.wallet || p.uid}
                    </TableCell>
                    {showSplit && (
                      <TableCell>
                        <span className="flex flex-wrap gap-1">
                          {payerFlavors(s).map((f) => (
                            <Badge key={f} variant={f === 'web2' ? 'default' : 'secondary'}>
                              {f}
                            </Badge>
                          ))}
                        </span>
                      </TableCell>
                    )}
                    <PayerProductsCell rows={split.payerProducts.get(p.uid) ?? []} />
                    <TableCell className="text-right">{p.count.toLocaleString()}</TableCell>
                    {showSplit && <FlavorCell stat={s.web2} />}
                    {showSplit && <FlavorCell stat={s.web3} />}
                    <TableCell className="text-right font-mono">{usd(p.netUsd)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
